import { tr } from "@/i18n/copy";
// Central Studio Profile consumer. These fallbacks are presentation-only and
// never write defaults back to a tenant record.
export const DEFAULT_STUDIO_BRANDING = Object.freeze({
  primary_color: "#D72F36",
  secondary_color: "#111113",
  accent_color: "",
  logo_path: null,
  brand_image_path: "",
  tagline: "",
  visual_style: "studio",
  background_treatment: "",
});

export function studioBranding(studio) {
  return { ...DEFAULT_STUDIO_BRANDING, ...(studio?.branding || {}) };
}

function hexToHslToken(value, fallback = "2 100% 44%") {
  const raw = String(value || "").trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return fallback;
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * (((b - r) / delta) + 2);
    else h = 60 * (((r - g) / delta) + 4);
  }
  if (h < 0) h += 360;
  const l = (max + min) / 2;
  const s = delta ? delta / (1 - Math.abs(2 * l - 1)) : 0;
  return `${Math.round(h * 10) / 10} ${Math.round(s * 1000) / 10}% ${Math.round(l * 1000) / 10}%`;
}

export function studioBrandStyle(studio) {
  const branding = clientAppPresentation(studio);
  const primary = hexToHslToken(branding.primary_color);
  const visualStyle = ["minimal", "bold"].includes(branding.visual_style) ? branding.visual_style : "studio";
  const typography = visualStyle === "minimal"
    ? '"Helvetica Neue", "Avenir Next", Inter, Arial, sans-serif'
    : visualStyle === "bold"
      ? 'Impact, "Arial Black", "Helvetica Neue", Arial, sans-serif'
      : 'Inter, "Helvetica Neue", Arial, sans-serif';
  return {
    "--studio-primary": branding.primary_color,
    "--studio-secondary": branding.secondary_color,
    "--studio-accent": branding.accent_color,
    "--accent": hexToHslToken(branding.accent_color),
    "--background": hexToHslToken(branding.secondary_color),
    "--card": hexToHslToken(branding.secondary_color),
    "--foreground": "0 0% 98%",
    "--card-foreground": "0 0% 98%",
    "--muted-foreground": "0 0% 72%",
    "--primary": primary,
    "--ring": primary,
    "--chart-1": primary,
    "--client-font": typography,
    "--client-heading-weight": visualStyle === "minimal" ? 500 : visualStyle === "bold" ? 900 : 700,
    "--client-card-radius": visualStyle === "minimal" ? "0.75rem" : visualStyle === "bold" ? "1.5rem" : "1.25rem",
    "--client-card-shadow": visualStyle === "minimal" ? "none" : visualStyle === "bold" ? "0 18px 45px rgba(0,0,0,.38)" : "0 12px 30px rgba(0,0,0,.24)",
    "--client-border-opacity": visualStyle === "minimal" ? ".28" : visualStyle === "bold" ? ".14" : ".18",
    "--client-letter-spacing": visualStyle === "minimal" ? ".015em" : visualStyle === "bold" ? "-.025em" : "0em",
  };
}

export function clientVisualStyleClass(studio) {
  const style = clientAppPresentation(studio).visual_style;
  return `client-visual-${style === "minimal" || style === "bold" ? style : "studio"}`;
}

export function studioDisplayName(studio) {
  return studio?.name || tr("Tattoo Pro Studio");
}

export function browserMediaUrl(value) {
  if (!value || typeof value !== "string") return "";
  if (/^(https?:|blob:|data:)/.test(value)) return value;
  if (!value.startsWith("/api/")) return "";
  return `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "")}${value}`;
}

// Public logo endpoints have an explicit PNG variant for OS installation.
// Keep version queries so changing a Studio/Brand logo changes its icon too.
export function clientInstallIconUrl(logo) {
  return typeof logo === "string" ? logo.replace(/(\/api\/public\/studios\/[^/]+\/assets\/)logo(?=\?|$)/, "$1logo-180.png") : "";
}

export function clientAppPresentation(studio, overrides = null) {
  const branding = studioBranding(studio);
  const app = { ...(studio?.client_app || {}), ...(overrides || {}) };
  const asset = (kind, path) => studio?.slug ? `/api/public/studios/${studio.slug}/assets/${kind}?v=${encodeURIComponent(String(path).split("/").pop())}` : "";
  const logoPath = app.public_logo_path || branding.logo_path || "";
  const heroPath = app.hero_image_path || "";
  return {
    ...app,
    primary_color: app.primary_color || DEFAULT_STUDIO_BRANDING.primary_color,
    secondary_color: app.secondary_color || DEFAULT_STUDIO_BRANDING.secondary_color,
    accent_color: app.accent_color || app.primary_color || DEFAULT_STUDIO_BRANDING.primary_color,
    tagline: app.tagline || "",
    visual_style: app.visual_style || "studio",
    public_logo_url: browserMediaUrl(logoPath ? asset("logo", logoPath) : ""),
    hero_image_url: browserMediaUrl(heroPath ? asset("hero", heroPath) : ""),
    overlay_strength: Number.isFinite(Number(app.overlay_strength)) ? Number(app.overlay_strength) : 55,
    sections: { artists: true, portfolio: true, qr: true, booking: true, social_contact: true, ...(app.sections || {}) },
  };
}

export function clientHeroStyle(studio, overrides = null) {
  const app = clientAppPresentation(studio, overrides);
  const branding = app;
  const opacity = Math.max(0, Math.min(90, app.overlay_strength)) / 100;
  return app.hero_image_url
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,${opacity}), rgba(0,0,0,${opacity})), url(${app.hero_image_url})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center top",
        backgroundColor: branding.secondary_color || "#111113",
      }
    : { backgroundColor: branding.secondary_color || "#111113" };
}

// HTTPS map links can hand off to native Maps and retain a browser fallback.
export function studioMapUrl(info = {}, device = typeof window !== "undefined" ? window.navigator : {}) {
  const address = [info.address, info.city, info.region].filter(value => typeof value === "string" && value.trim()).map(value => value.trim()).join(", ");
  if (!address) return "";
  const apple = /iPhone|iPad|iPod|Macintosh|Mac OS X/.test(device?.userAgent || "");
  const query = encodeURIComponent(address);
  return apple ? `https://maps.apple.com/?q=${query}` : `https://www.google.com/maps/search/?api=1&query=${query}`;
}
